// Error reporting (Sentry) for Studio and the web uploader — what goes WRONG, never a record of normal use.
//
// On only on a real deployment (window.__PH_ENV__ = staging | prod, injected by the SSR server; never a visitor's
// ?phEnv override, never localhost or tests). Captured: uncaught errors and rejections, failed API calls (status +
// path), a trail of the last steps (navigation, clicks, API calls, the uploader's own steps) and a 5 % sample of
// page-load / request timing. NOT captured: personal data — no emails, names, file or folder names, photos, tokens,
// IP addresses or console output; a person is only their Cognito `sub`. No session replay.
//
// The DSN is public by design (it is shipped to every browser); the auth token for source maps is a CI secret.
import * as Sentry from "@sentry/react";

const DSN = "https://592cecd5c583d3b5739d17a0a1e74156@o4511536432218112.ingest.de.sentry.io/4512361934946385";

let enabled = false;
export const telemetryEnabled = () => enabled;

/** The deployment this page belongs to (the SSR server's pinned env), or null off a real deployment. */
export const deploymentEnv = (w: { __PH_ENV__?: string; location?: { hostname?: string } } | undefined = typeof window !== "undefined" ? (window as never) : undefined): "staging" | "prod" | null => {
  if (!w) return null;
  const host = w.location?.hostname || "";
  if (!host || host === "localhost" || host.startsWith("127.") || host.endsWith(".local")) return null;
  return w.__PH_ENV__ === "staging" || w.__PH_ENV__ === "prod" ? w.__PH_ENV__ : null;
};

// ------------------------------------------------------------------------------------------------- scrubbing

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const BEARER = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
// A file name with a photo/video extension: runners' names live in these, often with spaces ("John Smith 042.JPG").
// A path (from its first slash or drive letter) goes whole; a bare name takes up to three words before it.
const EXT = "(?:jpe?g|png|heic|heif|webp|gif|tiff?|bmp|dng|cr2|cr3|nef|arw|raf|orf|rw2|mp4|mov|m4v|avi|mkv|zip)";
const FILE = new RegExp(`(?:[A-Za-z]:)?(?:[\\\\/][^\\\\/"'\\n]+)+\\.${EXT}\\b|(?:[^\\s"'/\\\\:]+ ){0,3}[^\\s"'/\\\\:]+\\.${EXT}\\b`, "gi");
const SECRET_KEY = /pass(word)?|pwd|secret|token|authorization|cookie|credential|email|name|path|file|key$/i;

/** Text with emails, tokens and file names replaced. */
export const scrubText = (s: string): string =>
  s.replace(JWT, "<jwt>").replace(BEARER, "$1 <redacted>").replace(EMAIL, "<email>").replace(FILE, "<file>");

/** A URL with its query string and fragment dropped (they carry tokens, emails and search terms). */
export const scrubUrl = (u: string): string => {
  const cut = u.split(/[?#]/)[0];
  return scrubText(cut);
};

/** Any value, recursively: secret-looking keys dropped, strings scrubbed. */
export const scrubValue = (v: unknown, depth = 0): unknown => {
  if (depth > 6) return "<deep>";
  if (typeof v === "string") return scrubText(v);
  if (Array.isArray(v)) return v.slice(0, 50).map((x) => scrubValue(x, depth + 1));
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = SECRET_KEY.test(k) ? "<redacted>" : scrubValue(x, depth + 1);
    return out;
  }
  return v;
};

type Crumb = Sentry.Breadcrumb;
export const scrubBreadcrumb = (b: Crumb): Crumb | null => {
  if (b.category === "console") return null; // console output can hold anything (e.g. a file name in a warning)
  const out: Crumb = { ...b };
  if (out.message) out.message = scrubText(out.message);
  if (out.data) {
    const d = { ...(out.data as Record<string, unknown>) };
    for (const k of ["url", "from", "to"]) if (typeof d[k] === "string") d[k] = scrubUrl(d[k] as string);
    for (const k of Object.keys(d)) if (!["url", "from", "to", "method", "status_code"].includes(k)) d[k] = scrubValue(d[k]);
    out.data = d;
  }
  return out;
};

export const scrubEvent = <E extends Sentry.ErrorEvent>(e: E): E => {
  if (e.message) e.message = scrubText(e.message);
  for (const ex of e.exception?.values ?? []) if (ex.value) ex.value = scrubText(ex.value);
  if (e.request) {
    if (e.request.url) e.request.url = scrubUrl(e.request.url);
    delete e.request.cookies;
    delete e.request.headers;
    delete e.request.query_string;
    delete e.request.data;
  }
  if (e.user) e.user = e.user.id ? { id: e.user.id } : undefined; // never an email, name or IP
  if (e.extra) e.extra = scrubValue(e.extra) as typeof e.extra;
  if (e.contexts) e.contexts = scrubValue(e.contexts) as typeof e.contexts;
  if (e.breadcrumbs) e.breadcrumbs = e.breadcrumbs.map(scrubBreadcrumb).filter((b): b is Crumb => !!b);
  return e;
};

// ------------------------------------------------------------------------------------------------- setup

/** Start error reporting on a real deployment. Safe to call more than once; a no-op elsewhere. */
export const initTelemetry = (): void => {
  if (enabled) return;
  const env = deploymentEnv();
  if (!env) return;
  let release: string | undefined;
  try { release = (import.meta.env.VITE_RELEASE as string | undefined) || undefined; } catch { release = undefined; }
  Sentry.init({
    dsn: DSN,
    environment: env === "prod" ? "production" : "staging",
    release,
    // Collect no user info, cookies, headers, query strings or request/response bodies (scrubbed again below).
    dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.05,
    maxBreadcrumbs: 100,
    // Noise that isn't ours: browser extensions, the post-deploy chunk reload (lib/chunkReload handles it), aborts.
    ignoreErrors: [
      /ResizeObserver loop/i,
      /Failed to fetch dynamically imported module/i,
      /Importing a module script failed/i,
      /Loading chunk \d+ failed/i,
      /AbortError/i,
      /Non-Error promise rejection captured/i,
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i, /^safari-(web-)?extension:\/\//i],
    beforeBreadcrumb: (b) => scrubBreadcrumb(b),
    beforeSend: (e) => scrubEvent(e),
    beforeSendTransaction: (t) => {
      if (t.request?.url) t.request.url = scrubUrl(t.request.url);
      if (t.transaction) t.transaction = scrubUrl(t.transaction);
      return t;
    },
  });
  enabled = true;
};

/** Who is signed in: their Cognito sub only (null when signed out). */
export const setTelemetryUser = (sub: string | null): void => {
  if (!enabled) return;
  Sentry.setUser(sub ? { id: sub } : null);
};

/** A step on the trail (shown with the next error or stall). Data: counts, ids and states only. */
export const step = (category: string, message: string, data?: Record<string, unknown>): void => {
  if (!enabled) return;
  Sentry.addBreadcrumb({ category, message, level: "info", data });
};

/** Report something that went wrong without throwing (an Error, or a short fixed message + counts). */
export const report = (what: unknown, context?: { tags?: Record<string, string>; extra?: Record<string, unknown>; level?: Sentry.SeverityLevel }): void => {
  if (!enabled) return;
  if (what instanceof Error) Sentry.captureException(what, { tags: context?.tags, extra: context?.extra, level: context?.level });
  else Sentry.captureMessage(String(what), { tags: context?.tags, extra: context?.extra, level: context?.level ?? "warning" });
};

export interface Watchdog {
  /** Progress happened: the clock starts again (and a stall already reported may report again later). */
  tick: (data?: Record<string, unknown>) => void;
  /** The work ended (finished, cancelled or failed): nothing more is reported. */
  stop: () => void;
}

/**
 * Something that should keep moving (reading folders, an upload pass): if `tick` isn't called for `ms`, one
 * "stalled" warning goes to Sentry with the trail and the last data given — a hang that never throws (the uploader
 * showing "working" for hours) is otherwise invisible. Reported once per quiet spell.
 */
export const watchdog = (stage: string, ms = 5 * 60_000, data: Record<string, unknown> = {}, now: () => number = Date.now): Watchdog => {
  let last = now();
  let reported = false;
  let latest = { ...data };
  let timer: ReturnType<typeof setInterval> | null = null;
  const check = () => {
    if (reported || now() - last < ms) return;
    reported = true;
    report(`stalled: ${stage}`, { tags: { stalled: stage }, extra: { ...latest, quietMs: now() - last } });
  };
  if (enabled) timer = setInterval(check, Math.min(ms, 30_000));
  return {
    tick: (d) => { last = now(); reported = false; if (d) latest = { ...latest, ...d }; },
    stop: () => { if (timer) clearInterval(timer); timer = null; },
  };
};

export { Sentry };
